import { Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLeadReplies } from '@/services/api';
import type { LeadReply } from '@/types';
import { PaperClipIcon } from '@heroicons/react/24/outline';

interface LeadRepliesThreadProps {
  leadId: string;
  /** Snapshot of the current user's last-read time captured before mark-read was called.
   *  undefined = request still in-flight (no highlights yet)
   *  null      = user has never read this lead (all inbound are new)
   *  string    = ISO timestamp; messages after this time are new */
  lastReadAt?: string | null;
  contactName?: string;
}

function isNewMessage(reply: LeadReply, lastReadAt: string | null | undefined): boolean {
  if (lastReadAt === undefined) return false;
  if (reply.direction !== 'Inbound') return false;
  if (lastReadAt === null) return true;
  return new Date(reply.occurredAt) > new Date(lastReadAt);
}

/**
 * Displays chronological conversation history for a lead
 * Shows replies with author, timestamp, content, and marketing material attachments
 */
export default function LeadRepliesThread({ leadId, lastReadAt, contactName }: LeadRepliesThreadProps) {
  const { data: replies, isLoading, isError } = useQuery({
    queryKey: ['lead-replies', leadId],
    queryFn: () => getLeadReplies(leadId),
  });

  if (isLoading) {
    return (
      <div className="rounded-xl border border-brand-border bg-white p-4 shadow-sm">
        <p className="text-sm text-gray-500">Loading conversation history...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 shadow-sm">
        <p className="text-sm text-red-600">Failed to load conversation history.</p>
      </div>
    );
  }

  if (!replies || replies.length === 0) {
    return (
      <div className="rounded-xl border border-brand-border bg-gray-50 p-4 shadow-sm">
        <p className="text-sm text-gray-500 italic">No replies yet. Send the first reply above.</p>
      </div>
    );
  }

  // Sort replies in descending order (newest first)
  const sortedReplies = [...replies].sort((a, b) => 
    new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime()
  );

  const firstNewIdx = sortedReplies.findIndex((reply) => isNewMessage(reply, lastReadAt));

  return (
    <div className="rounded-xl border border-brand-border bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-base font-semibold text-brand-text-primary">Conversation</h3>
        <span className="text-xs text-brand-text-secondary">{sortedReplies.length} messages</span>
      </div>
      
      <div className="space-y-3">
      {sortedReplies.map((reply, idx) => {
        const isInbound = reply.direction === 'Inbound';
        const isNew = isNewMessage(reply, lastReadAt);
        const senderLabel = isInbound ? (contactName ?? 'Customer') : 'Cre8or';
        const statusLabel = reply.deliveryStatus === 'Received' ? 'Replied' : reply.deliveryStatus;
        const showDivider = idx === firstNewIdx && firstNewIdx !== -1;

        return (
          <Fragment key={reply.eventId}>
            {showDivider && (
              <div className="flex items-center gap-3 py-1">
                <div className="flex-1 border-t border-brand-accent/40" />
                <span className="rounded-full bg-brand-accent/15 px-3 py-1 text-xs font-semibold text-brand-accent">
                  New messages
                </span>
                <div className="flex-1 border-t border-brand-accent/40" />
              </div>
            )}

            <div
              className={`flex ${isInbound ? 'justify-start' : 'justify-end'}`}
            >
              <div
              className={`w-full max-w-[85%] rounded-2xl border px-4 py-3 shadow-sm ${
                isNew
                  ? 'bg-brand-accent/10 border-brand-accent/40 ring-1 ring-brand-accent/30'
                  : isInbound
                  ? 'bg-white border-gray-200'
                  : 'bg-blue-50 border-blue-200'
              }`}
            >
              <div className="mb-2 flex items-center justify-between gap-3 text-xs text-gray-500">
                <div className="min-w-0">
                  <span className={`font-semibold ${isInbound ? 'text-brand-text-primary' : 'text-blue-700'}`}>
                    {senderLabel}
                  </span>
                  {isInbound && reply.senderRef && (
                    <span className="ml-1 text-gray-400 font-normal">({reply.senderRef})</span>
                  )}
                  <span className="ml-2">{new Date(reply.occurredAt).toLocaleString()}</span>
                </div>
                <span className={`px-2 py-0.5 rounded font-medium ${
                  reply.deliveryStatus === 'Received'  ? 'bg-blue-100 text-blue-700' :
                  reply.deliveryStatus === 'Delivered' ? 'bg-green-100 text-green-700' :
                  reply.deliveryStatus === 'Failed'    ? 'bg-red-100 text-red-700' :
                  reply.deliveryStatus === 'Sent'      ? 'bg-gray-100 text-gray-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {statusLabel}
                </span>
              </div>

              {reply.subject && (
                <h4 className="mb-2 text-sm font-semibold text-brand-text-primary">{reply.subject}</h4>
              )}

              <div className="whitespace-pre-wrap text-sm text-gray-700">
                {reply.bodyText}
              </div>

              {reply.attachments && reply.attachments.length > 0 && (
                <div className="mt-3 border-t border-gray-200 pt-3">
                  <p className="mb-2 text-xs font-medium text-gray-600">Attachments</p>
                  <ul className="flex flex-wrap gap-2">
                    {reply.attachments.map(attachment => (
                      <li 
                        key={attachment.attachmentId}
                        className="flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs"
                      >
                        <PaperClipIcon className="h-3.5 w-3.5 text-gray-500" title="Attachment" />
                        <a
                          href={`/api/marketing-materials/${attachment.sourceMaterialId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-blue-600 hover:underline"
                          title={`Download ${attachment.fileName}`}
                        >
                          {attachment.fileName}
                        </a>
                        <span className="text-gray-400">
                          ({(attachment.fileSizeBytes / 1024).toFixed(1)} KB)
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            </div>

          </Fragment>
        );
      })}
      </div>
    </div>
  );
}
